import type {ShopCity, ShopClinic, ShopClinicQuote, ShopFreshness} from '~/types/shopStorefront'
import {declineWord} from '~/utils/number'

/** Адреса витрины. Корень каталога — `/`; на локали `useShopPaths` добавляет `/katalog`. */
export const shopPaths = {
    catalog: () => '/',
    section: (section: string) => `/${section}/`,
    category: (section: string, category: string) => `/${section}/${category}/`,
    animal: (section: string, category: string, animal: string) => `/${section}/${category}/${animal}/`,
    brand: (brand: string) => `/brendy/${brand}/`,
    product: (slug: string) => `/tovar/${slug}/`,
    cart: () => '/korzina/',
    checkout: () => '/korzina/oformlenie/',
    booking: (token: string) => `/bron/${token}/`,
}

/** Срез по животному — последний сегмент листинга; ключи совпадают с ShopAnimalEnum бэкенда. */
export const SHOP_ANIMAL_TITLES: Record<string, string> = {
    koshki: 'для кошек',
    sobaki: 'для собак',
    'gryzuny-i-kroliki': 'для грызунов и кроликов',
    horki: 'для хорьков',
    pticy: 'для птиц',
    reptilii: 'для рептилий',
}

/** Срез по животному индексируется, только если в нём набралось столько товаров (SEO v1.4). */
export const SHOP_ANIMAL_INDEX_MIN_PRODUCTS = 5

export const SHOP_SLUG_PATTERN = /^[a-z0-9-]+$/

export function isShopSlug(value: unknown): value is string {
    return typeof value === 'string' && SHOP_SLUG_PATTERN.test(value)
}

/** Остаток клиники честно стареет вместе с последней выгрузкой склада. */
export function shopFreshnessLabel(freshness: ShopFreshness, syncedAt: string | null, now = Date.now()): string {
    if (freshness === 'unconfirmed') {
        return 'Наличие на уточнении'
    }

    if (freshness === 'fresh') {
        return 'В наличии'
    }

    if (freshness === 'recent' && syncedAt) {
        const hours = Math.max(1, Math.floor((now - new Date(syncedAt).getTime()) / 3_600_000))
        return `Обновлено ${hours} ${declineWord(hours, ['час', 'часа', 'часов'])} назад`
    }

    return 'Скорее всего есть — уточните в клинике'
}

/** «в Москве» — город в предложном падеже для заголовков и описаний. */
export function shopCityIn(city: ShopCity): string {
    return `в ${city.nameCases?.prepositional_singular ?? city.name}`
}

/** Карточка клиники на основном сайте. На витрине своего адреса у клиники нет. */
export function shopClinicPath(clinic: Pick<ShopClinic, 'citySlug' | 'slug'>): string | null {
    return clinic.citySlug ? `/clinics/${clinic.citySlug}/${clinic.slug}/` : null
}

export interface ShopMapPoint {
    id: number
    name: string
    coordinates: [number, number]
}

type ShopLatLng = {latitude: number | null, longitude: number | null} | null | undefined

/** Яндекс ждёт [долгота, широта]. Ноль и пустое значение точкой не считаются. */
function shopLngLat(location: ShopLatLng): [number, number] | null {
    const latitude = location?.latitude
    const longitude = location?.longitude

    if (latitude == null || longitude == null || latitude === 0 || longitude === 0) {
        return null
    }

    return [longitude, latitude]
}

/** Пины клиник: только точки с координатами, без повторов. */
export function shopClinicMapPoints(clinics: Array<Pick<ShopClinic, 'id' | 'name' | 'location'>>): ShopMapPoint[] {
    const points: ShopMapPoint[] = []
    const seen = new Set<number>()

    for (const clinic of clinics) {
        if (seen.has(clinic.id)) {
            continue
        }

        const coordinates = shopLngLat(clinic.location)
        if (!coordinates) {
            continue
        }

        seen.add(clinic.id)
        points.push({id: clinic.id, name: clinic.name, coordinates})
    }

    return points
}

/** Камера карты, когда у клиник нет своей точки: центр города из карточки. */
export function shopClinicMapCenter(clinics: Array<Pick<ShopClinic, 'cityLocation'>>): [number, number] | null {
    for (const clinic of clinics) {
        const coordinates = shopLngLat(clinic.cityLocation)
        if (coordinates) {
            return coordinates
        }
    }

    return null
}

export function shopCartMapPoints(quotes: ShopClinicQuote[]): ShopMapPoint[] {
    return shopClinicMapPoints(quotes.map((quote) => quote.clinic))
}

export function shopClinicsLabel(count: number): string {
    return `в ${count} ${declineWord(count, ['клинике', 'клиниках', 'клиниках'])}`
}

/** Ошибки валидации бэкенда по полям: первое сообщение каждого поля, общее — под ключом `message`. */
export function shopApiErrors(error: unknown): Record<string, string> {
    const {message, errors} = (error ?? {}) as {message?: string, errors?: Record<string, string[]>}
    const fields = Object.fromEntries(Object.entries(errors ?? {}).map(([field, messages]) => [field, messages[0] ?? '']))

    return Object.keys(fields).length ? fields : {message: message || 'Что-то пошло не так. Попробуйте ещё раз.'}
}

export function shopHoldLabel(hours: number | null): string {
    if (!hours) {
        return ''
    }

    return `Бронь держится ${hours} ${declineWord(hours, ['час', 'часа', 'часов'])}`
}
